import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Play, Calendar, ChevronRight, Film } from "lucide-react";
import { API } from "@/config";

export default function SeasonPage() {
  const { seasonId } = useParams();
  const [season, setSeason] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchSeasonData();
  }, [seasonId]); // eslint-disable-line react-hooks/exhaustive-deps -- refetch on season change
  
  const fetchSeasonData = async () => {
    try {
      const [seasonRes, videosRes] = await Promise.all([
        axios.get(`${API}/seasons/${seasonId}`),
        axios.get(`${API}/videos?season_id=${seasonId}`)
      ]);
      setSeason(seasonRes.data);
      setVideos(videosRes.data);
    } catch (err) {
      console.error("Error fetching season:", err);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  if (!season) {
    return (
      <div className="container" style={{ padding: '4rem 1rem' }}>
        <div className="empty-state">
          <h3 className="empty-state-title">Season Not Found</h3>
          <Link to="/vault" className="btn btn-primary" style={{ marginTop: '1rem' }}>
            Back to The Vault
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container" style={{ padding: '2rem 1rem 4rem' }}>
      <nav className="breadcrumb">
        <Link to="/vault">The Vault</Link>
        <ChevronRight size={16} className="breadcrumb-separator" />
        <span>{season.year}</span>
      </nav>
      
      <div className="section-header">
        <div>
          <h1 className="section-title" style={{ fontSize: '2rem' }}>{season.year} Season</h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>{season.title}</p>
        </div>
        <span className="btn btn-secondary" style={{ cursor: 'default' }}>
          <Play size={16} />
          {videos.length} {videos.length === 1 ? 'Video' : 'Videos'}
        </span>
      </div>
      
      {videos.length === 0 ? (
        <div className="empty-state">
          <Film className="empty-state-icon" />
          <h3 className="empty-state-title">No Videos Yet</h3>
          <p className="empty-state-text">Videos for this season will be added soon.</p>
        </div>
      ) : (
        <div className="video-grid">
          {videos.map((video) => (
            <Link 
              key={video.id} 
              to={`/watch/${video.id}`} 
              className="video-card"
              data-testid={`video-card-${video.id}`}
            >
              <div className="video-card-thumbnail">
                <img 
                  src={video.thumbnail || 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400&q=80'} 
                  alt={video.title}
                />
                <div className="video-card-play">
                  <div className="video-card-play-icon">
                    <Play size={24} />
                  </div>
                </div>
                {video.duration && (
                  <span className="video-card-duration">{video.duration}</span>
                )}
                <span className="video-card-type">
                  {video.video_type === 'documentary' ? 'Doc' : 'Game'}
                </span>
              </div>
              <div className="video-card-body">
                <h3 className="video-card-title">{video.title}</h3>
                <div className="video-card-meta">
                  <span className="video-card-meta-item">
                    <Calendar size={14} />
                    {video.date}
                  </span>
                  {video.opponent && (
                    <span className="video-card-meta-item">
                      vs {video.opponent}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
